import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  SettingsField,
  SettingsInput,
  SettingsPageHeader,
  SettingsSection,
  SettingsToggleRow,
} from "@/components/settings/settings-ui";
import {
  getEnrichmentWaterfall,
  putEnrichmentWaterfall,
} from "@/api/enrichment";

export function SettingsOrgConfigPage() {
  const qc = useQueryClient();
  const [toggles, setToggles] = useState({
    dedupe: true,
    excludeCustomers: true,
    requireCompany: false,
    waterfalls: true,
  });
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const waterfallQuery = useQuery({
    queryKey: ["enrichment", "waterfall"],
    queryFn: getEnrichmentWaterfall,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!waterfallQuery.data) return;
    setToggles((t) => ({
      ...t,
      waterfalls:
        waterfallQuery.data.waterfallEmail || waterfallQuery.data.waterfallPhone,
    }));
  }, [waterfallQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      putEnrichmentWaterfall({
        waterfallEmail: toggles.waterfalls,
        waterfallPhone: toggles.waterfalls,
        providerOrder: waterfallQuery.data?.providerOrder ?? ["apollo"],
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["enrichment"] });
      setSaveMessage("Org config saved.");
    },
    onError: (err: unknown) => {
      setSaveMessage(err instanceof Error ? err.message : "Save failed");
    },
  });

  return (
    <div>
      <SettingsPageHeader
        title="Org config"
        description="Rules of engagement for hunt, lists, and signal sources."
        onSave={() => saveMutation.mutate()}
        saving={saveMutation.isPending}
      />

      {saveMessage ? (
        <p className="mb-4 text-sm text-muted-foreground">{saveMessage}</p>
      ) : null}

      <SettingsSection
        title="Discovery defaults"
        description="Applied when searching people in Find & enrich and building lists."
      >
        <SettingsToggleRow
          label="Deduplicate people already in my leads"
          checked={toggles.dedupe}
          onChange={(v) => setToggles((t) => ({ ...t, dedupe: v }))}
        />
        <SettingsToggleRow
          label="Exclude existing customers from discovery"
          checked={toggles.excludeCustomers}
          onChange={(v) => setToggles((t) => ({ ...t, excludeCustomers: v }))}
        />
        <SettingsToggleRow
          label="Require a company match before saving a person"
          checked={toggles.requireCompany}
          onChange={(v) => setToggles((t) => ({ ...t, requireCompany: v }))}
        />
        <SettingsToggleRow
          label="Use enrichment waterfalls for email & phone"
          checked={toggles.waterfalls}
          onChange={(v) => setToggles((t) => ({ ...t, waterfalls: v }))}
        />
        <SettingsField
          label="Default list board"
          hint="Used when saving people from People search."
        >
          <SettingsInput defaultValue="Tracked accounts" />
        </SettingsField>
      </SettingsSection>

      <SettingsSection title="Related surfaces">
        <div className="grid gap-2 sm:grid-cols-3">
          <Link
            to="/enrichment"
            className="rounded-md border border-border px-3 py-3 text-sm transition hover:bg-muted/10"
          >
            <p className="font-medium">Data Health Center</p>
            <p className="text-xs text-muted-foreground">
              Coverage, queues, credits
            </p>
          </Link>
          <Link
            to="/lists"
            className="rounded-md border border-border px-3 py-3 text-sm transition hover:bg-muted/10"
          >
            <p className="font-medium">Lists</p>
            <p className="text-xs text-muted-foreground">
              Tracked accounts & boards
            </p>
          </Link>
          <Link
            to="/settings/signals"
            className="rounded-md border border-border px-3 py-3 text-sm transition hover:bg-muted/10"
          >
            <p className="font-medium">Signals</p>
            <p className="text-xs text-muted-foreground">Provider catalog</p>
          </Link>
        </div>
      </SettingsSection>
    </div>
  );
}
